import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    FormHelperText,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Check as CheckIcon,
    PlayArrow as PlayArrowIcon,
    AddCircle as AddCircleIcon,
} from '@mui/icons-material';
import { useVideosQuery } from '../../stores/tanstack/queries/useVideosQuery';

const VideoLibrarySelection = ({ open, onClose, onConfirm }) => {
    const { t } = useTranslation();  // Translation hook

    const [selectedVideos, setSelectedVideos] = useState(new Set());
    const [activeTab, setActiveTab] = useState('dd');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPreference, setSelectedPreference] = useState('all');
    const [selectOpen, setSelectOpen] = useState(false);
    const [wasEverSelected, setWasEverSelected] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoToPlay, setVideoToPlay] = useState(null);

    const {
        data: videosData,
        isLoading: videosLoading,
        error: videosError,
    } = useVideosQuery();

    const { allVideos, physioVideos } = useMemo(
        () => ({
            allVideos: videosData?.allVideos || [],
            physioVideos: videosData?.physioVideos || [],
        }),
        [videosData?.allVideos, videosData?.physioVideos]
    );

    const allCurrentVideos = useMemo(() => {
        return activeTab === 'dd' ? allVideos : physioVideos;
    }, [activeTab, allVideos, physioVideos]);

    const uniquePreferences = useMemo(() => {
        const prefsSet = new Set();
        allCurrentVideos.forEach((video) => {
            video.preferences?.forEach((pref) => {
                prefsSet.add(pref.id);
            });
        });
        return Array.from(prefsSet);
    }, [allCurrentVideos]);

    const filteredVideos = useMemo(() => {
        let videos = [...allCurrentVideos];

        if (selectedPreference && selectedPreference !== 'all') {
            videos = videos.filter((video) =>
                video.preferences?.some((pref) => pref.id === selectedPreference)
            );
        }

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            videos = videos.filter(
                (video) =>
                    video.title?.toLowerCase().includes(lowerSearch) ||
                    video.description?.toLowerCase().includes(lowerSearch) ||
                    video.preferences?.some((pref) =>
                        pref.name.toLowerCase().includes(lowerSearch)
                    )
            );
        }

        return videos;
    }, [allCurrentVideos, selectedPreference, searchTerm]);

    const handlePreferenceChange = useCallback((event) => {
        setSelectedPreference(event.target.value);
        setWasEverSelected(true);
    }, []);

    const handleOpenSelect = useCallback(() => {
        setSelectOpen(true);
    }, []);

    const handleCloseSelect = useCallback(() => {
        setSelectOpen(false);
    }, []);

    const toggleVideoSelection = useCallback((videoId) => {
        setSelectedVideos((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(videoId)) {
                newSet.delete(videoId);
            } else {
                newSet.add(videoId);
            }
            return newSet;
        });
    }, []);

    const handlePlayVideo = useCallback((video) => {
        setVideoToPlay(video);
        setIsPlaying(true);
    }, []);

    const handleConfirm = () => {
        const allVideosPool = [...allVideos, ...physioVideos];
        const selected = allVideosPool.filter((video) =>
            selectedVideos.has(video.id)
        );
        onConfirm(selected);

        setSelectedVideos(new Set());
        setSearchTerm('');
        setSelectedPreference('all');
        setActiveTab('dd');
        setWasEverSelected(false);
        onClose();
    };

    const handleCancel = () => {
        setSelectedVideos(new Set());
        setSearchTerm('');
        setSelectedPreference('all');
        setActiveTab('dd');
        setWasEverSelected(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#00023a',
                    borderRadius: 2,
                    p: 3,
                    maxHeight: '90vh',
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    pb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {t('videoLibrary')}
                <IconButton
                    onClick={handleCancel}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                            color: 'white',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                {/* Header with Tabs and Video Count */}
                <Box sx={{ mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontWeight: 600,
                                minWidth: 0,
                                '&.Mui-selected': {
                                    color: 'white',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'white',
                            },
                            '& .MuiTabScrollButton-root': {
                                color: 'white',
                            },
                        }}
                    >
                        <Tab label={t('deskDynamicsVideos')} value="dd" />
                        <Tab label={t('physioVideos')} value="physio" />
                    </Tabs>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: 0.5,
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.875rem',
                        }}
                    >
                        {t('videoCount', { count: filteredVideos.length })}
                    </Typography>
                </Box>

                {/* Search and Filter Bar */}
                <Grid container spacing={2} style={{ marginBottom: '20px' }}>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            placeholder={t('search')}
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    color: 'black',
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#1976d2',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: 'black',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <Select
                                value={selectedPreference}
                                onChange={handlePreferenceChange}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected || selected === 'all') {
                                        return t('selectAll');
                                    } else {
                                        const pref = allCurrentVideos.reduce((foundPref, video) => {
                                            return (
                                                foundPref ||
                                                video.preferences?.find((pref) => pref.id === selected)
                                            );
                                        }, null);
                                        return pref ? pref.name : t('selectAll');
                                    }
                                }}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'transparent',
                                    border: '1px solid white',
                                    borderRadius: 1,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: 'white',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: 'white',
                                            '& .MuiMenuItem-root': {
                                                color: 'black',
                                            },
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="all">{t('selectAll')}</MenuItem>
                                {uniquePreferences.map((prefId) => {
                                    const pref = allCurrentVideos.reduce((foundPref, video) => {
                                        return (
                                            foundPref ||
                                            video.preferences?.find((pref) => pref.id === prefId)
                                        );
                                    }, null);

                                    return (
                                        pref && (
                                            <MenuItem key={pref.id} value={pref.id}>
                                                {pref.name}
                                            </MenuItem>
                                        )
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Loading State */}
                {videosLoading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="400px"
                    >
                        <CircularProgress sx={{ color: 'white' }} />
                        <Typography variant="h6" color="white" sx={{ ml: 2 }}>
                            {t('loadingVideos')}
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {videosError && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="400px"
                    >
                        <Typography variant="h6" color="error">
                            {videosError.message || t('errorLoadingVideos')}
                        </Typography>
                    </Box>
                )}

                {/* Video Grid Display */}
                {!videosLoading && !videosError && (
                    <Grid container spacing={2} className="libraryGrid">
                        {Array.isArray(filteredVideos) && filteredVideos.length > 0 ? (
                            filteredVideos.map((video) => (
                                <Grid
                                    item
                                    key={video.id}
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    style={{
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                    }}
                                >
                                    <Card
                                        style={{
                                            height: '350px',
                                            cursor: 'pointer',
                                            backgroundColor: 'rgba(0, 0, 0, 0)',
                                            position: 'relative',
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            alt={video.title}
                                            height="200"
                                            image={`${process.env.REACT_APP_FIREBASE_API_HOST}/api/thumbnails/${video.externalId}`}
                                            loading="lazy"
                                            sx={{
                                                objectFit: 'cover',
                                                backgroundColor: '#000',
                                            }}
                                        />
                                        <CardContent
                                            style={{
                                                flex: 1,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                backgroundColor: 'transparent',
                                                color: 'white',
                                            }}
                                        >
                                            <Typography variant="caption" style={{ color: 'white' }}>
                                                {video.preferences?.map((pref) => pref.name).join(', ')}
                                            </Typography>
                                            <Typography variant="h6" style={{ color: 'white' }}>
                                                {video.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color: 'white',
                                                    maxHeight: '4em',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {video.description}
                                            </Typography>
                                        </CardContent>

                                        {/* Hover Overlay with Actions */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                opacity: 0,
                                                transition: 'opacity 0.3s',
                                                '&:hover': { opacity: 1 },
                                            }}
                                        >
                                            <IconButton
                                                sx={{ color: 'white', mr: 2 }}
                                                onClick={() => handlePlayVideo(video)}
                                            >
                                                <PlayArrowIcon fontSize="large" />
                                            </IconButton>
                                            <IconButton
                                                sx={{ color: 'white' }}
                                                onClick={() => toggleVideoSelection(video.id)}
                                            >
                                                <AddCircleIcon fontSize="large" />
                                            </IconButton>
                                        </Box>

                                        {/* Selected Overlay */}
                                        {selectedVideos.has(video.id) && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <IconButton
                                                    sx={{ color: 'white' }}
                                                    onClick={() => toggleVideoSelection(video.id)}
                                                >
                                                    <CheckIcon
                                                        style={{ fontSize: '4rem', color: 'white' }}
                                                    />
                                                </IconButton>
                                            </div>
                                        )}
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Typography color="white" align="center">
                                    {activeTab === 'physio'
                                        ? t('noPhysioVideos')
                                        : t('noVideosFound')}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>

            {/* Footer */}
            <Box sx={{ px: 3, pb: 2 }}>
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.9rem',
                        mb: 2,
                    }}
                >
                    {t('selectedVideosCount', { count: selectedVideos.size })}
                </Typography>

                <DialogActions sx={{ p: 0, gap: 2 }}>
                    <Button
                        onClick={handleCancel}
                        sx={{
                            flex: 1,
                            bgcolor: '#4a5d74',
                            color: 'white',
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            '&:hover': {
                                bgcolor: '#3d4e63',
                            },
                        }}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={selectedVideos.size === 0}
                        sx={{
                            flex: 1,
                            bgcolor: '#2563EB',
                            color: 'white',
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            '&:hover': {
                                bgcolor: '#1D4ED8',
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#4a5d74',
                                color: 'rgba(255, 255, 255, 0.5)',
                            },
                        }}
                    >
                        {t('confirm')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default VideoLibrarySelection;